import React, { useState } from "react";

const PieChart = ({ data, isLoading, size = 250, showTotal = true }) => {
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const safeTotal = total === 0 ? 1 : total;

  const nonZeroSlices = data.filter((item) => item.value > 0);
  const onlyOneSlice = nonZeroSlices.length === 1;

  let cumulative = 0;

  const radius = size / 2;
  const center = size / 2;

  const getCoordinatesForPercent = (percent) => {
    const x = Math.cos(2 * Math.PI * percent) * radius + center;
    const y = Math.sin(2 * Math.PI * percent) * radius + center;
    return [x, y];
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[200px] w-full">
        <div className="mini-spinner mt-3"></div>
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] w-full">
        No data available
      </div>
    );
  }

  return (
    <div className="relative flex-col inline-flex justify-center items-center w-full">
      <div className="relative">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="drop-shadow-md"
          onMouseMove={handleMouseMove}
        >
          {data.map((slice, index) => {
            const value = slice.value > 0 ? slice.value : 0;

            if (onlyOneSlice && value > 0) {
              return (
                <circle
                  key={index}
                  cx={center}
                  cy={center}
                  r={radius}
                  fill={slice.color}
                  onMouseEnter={(e) => setHovered({ ...slice, index, e })}
                  onMouseLeave={() => setHovered(null)}
                />
              );
            }

            if (value === 0) return null;

            const [startX, startY] = getCoordinatesForPercent(cumulative / safeTotal);
            cumulative += value;
            const [endX, endY] = getCoordinatesForPercent(cumulative / safeTotal);

            const largeArcFlag = value / safeTotal > 0.5 ? 1 : 0;

            const pathData = `M ${center} ${center} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;

            return (
              <path
                key={index}
                d={pathData}
                fill={slice.color}
                stroke="#fff"
                strokeWidth="0"
                className="transition-transform duration-300 cursor-pointer"
                onMouseEnter={(e) => setHovered({ ...slice, index, e })}
                onMouseLeave={() => setHovered(null)}
              />
            );
          })}

          {showTotal && (
            <>
              <circle cx={center} cy={center} r={radius * 0.6} fill="white" />
              <text
                x={center}
                y={center - size * 0.05}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-gray-500 font-medium"
                style={{ fontSize: `${size * 0.08}px` }}
              >
                Total
              </text>
              <text
                x={center}
                y={center + size * 0.1}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-gray-900 font-medium"
                style={{ fontSize: `${size * 0.15}px` }}
              >
                {total}
              </text>
            </>
          )}
        </svg>

        {hovered && (
          <div
            className="absolute bg-white text-sm px-3 py-2 rounded-lg shadow-lg pointer-events-none whitespace-nowrap"
            style={{ top: `${mousePos.y - 40}px`, left: `${mousePos.x + 15}px` }}
          >
            <div className="font-medium">
              {hovered.label} : <span className="text-[#245CEA] font-semibold">{hovered.value}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-center items-center gap-8 mt-[54px]">
        {data.map((slice, index) => (
          <div key={index} className="flex items-center space-x-[10px]">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: slice.color }}></div>
            <div className="text-[#373737] font-mont font-semibold lowercase cursor-pointer first-letter:uppercase">
              {slice.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PieChart;